import { createSignal, createResource, createMemo, createEffect, onCleanup, For, Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import MinecraftText from '../components/MinecraftText';
import SellbotNav from '../components/SellbotNav';
import { Pagination } from '../components/Pagination';
import { DateRangePills } from '../components/DateRangePills';
import SellbotZoneFilter from '../components/SellbotZoneFilter';
import { useToast } from '../components/MessageBox';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useActiveBot } from '../state/activeBot';
import { useTranslation } from '../hooks/useTranslation';
import { useDebounced } from '../hooks/useDebounced';
import { useSellbotReason } from '../hooks/useSellbotReason';
import { useSellbotZones, ZONE_FILTER_ALL } from '../hooks/useSellbotZones';
import { formatDateTime } from '../services/dateFormat';
import { subscribeBotEngineEvents } from '../services/botEngineEvents';
import { ankaufItemsSummary } from '../services/ankaufItemsSummary';
import type { SellbotTransactionsResponse } from '../api';
import { sellbotStatusClass } from '../services/sellbotStatus';
import styles from './Sellbot.module.css';

const ALL_STATUSES = ['Received', 'Processing', 'Completed', 'Refunded', 'OutOfStock', 'PayerMismatch', 'NoMatch', 'Ignored', 'ItemDisabled', 'Failed', 'Cancelled', 'Delivering', 'LimitReached'] as const;

type KindFilter = 'all' | 'sale' | 'ankauf';
type QuickFilter = 'all' | 'today' | '7d' | '30d' | 'custom';

const QUICK_PILLS = [
    { value: 'all' as QuickFilter, labelKey: 'DATE_RANGE_ALL' },
    { value: 'today' as QuickFilter, labelKey: 'DATE_RANGE_TODAY' },
    { value: '7d' as QuickFilter, labelKey: 'DATE_RANGE_7D' },
    { value: '30d' as QuickFilter, labelKey: 'DATE_RANGE_30D' },
    { value: 'custom' as QuickFilter, labelKey: 'SELLBOT_STATS_RANGE_CUSTOM' },
];

const SKELETON_ROW_COUNT = 8;
// A phone with the offline banner open leaves room for one row; below this the list scrolls instead.
const MIN_ROWS_PER_PAGE = 5;

export default function SellbotTransactions() {
    const api = useApi();
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation();
    const names = useSellbotReason();
    const { botId } = useActiveBot();
    const { multiZone, zones, zoneLabel } = useSellbotZones(botId);

    const fmtNum = (n: number) => new Intl.NumberFormat('de-DE').format(n);
    const fmtMoney = (n: number) => `${fmtNum(n)}$`;

    const [quickFilter, setQuickFilter] = createSignal<QuickFilter>('all');
    const [customFrom, setCustomFrom] = createSignal('');
    const [customTo, setCustomTo] = createSignal('');
    const [kindFilter, setKindFilter] = createSignal<KindFilter>('all');
    const [statusFilter, setStatusFilter] = createSignal('');
    const [zoneFilter, setZoneFilter] = createSignal(ZONE_FILTER_ALL);
    const [search, setSearch] = createSignal('');
    const debouncedSearch = useDebounced(search, 250);
    const [page, setPage] = createSignal(0);
    const [rowsPerPage, setRowsPerPage] = createSignal<number>();
    const [listEl, setListEl] = createSignal<HTMLDivElement>();

    const rangeFor = (quick: QuickFilter, from: string, to: string): { from?: string; to?: string } => {
        const now = new Date();
        switch (quick) {
            case 'today': return { from: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString() };
            case '7d': return { from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString() };
            case '30d': return { from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString() };
            case 'custom': return {
                from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
                to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
            };
            default: return {};
        }
    };

    const [feed, { refetch: refetchFeed }] = createResource(
        () => {
            const id = botId();
            const pageSize = rowsPerPage();
            if (!id || !pageSize) return false;
            return {
                id,
                pageSize,
                page: page(),
                kind: kindFilter(),
                status: statusFilter(),
                zone: zoneFilter(),
                search: debouncedSearch().trim(),
                ...rangeFor(quickFilter(), customFrom(), customTo()),
            };
        },
        async (params) => {
            const res = await api.sellbot.getTransactions(params.id, {
                page: params.page + 1,
                pageSize: params.pageSize,
                kind: params.kind === 'all' ? undefined : params.kind,
                status: params.status || undefined,
                zone: params.zone || undefined,
                search: params.search || undefined,
                from: params.from,
                to: params.to,
            });
            if (isOk(res.data)) return res.data.value;
            toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FAILED'));
            return { total: 0, page: 1, pageSize: params.pageSize, entries: [] } satisfies SellbotTransactionsResponse;
        }
    );

    const totalPages = createMemo(() => {
        const feedPage = feed.latest;
        return feedPage ? Math.max(1, Math.ceil(feedPage.total / feedPage.pageSize)) : 1;
    });

    createEffect(() => {
        const lastPage = totalPages() - 1;
        if (feed.latest && page() > lastPage) setPage(lastPage);
    });

    const fitRowsToList = () => {
        const list = listEl();
        const firstRow = list?.firstElementChild as HTMLElement | null;
        if (!list || !firstRow || !firstRow.classList.contains(styles.txnRowMerged) || firstRow.offsetHeight === 0) return;
        const rowGap = parseFloat(getComputedStyle(list).rowGap) || 0;
        const fittingRows = Math.max(MIN_ROWS_PER_PAGE, Math.floor((list.clientHeight + rowGap) / (firstRow.offsetHeight + rowGap)));
        const previousRows = rowsPerPage();
        if (fittingRows === previousRows) return;
        if (previousRows) setPage(Math.floor(page() * previousRows / fittingRows));
        setRowsPerPage(fittingRows);
    };

    createEffect(() => {
        const list = listEl();
        if (!list) return;
        const observer = new ResizeObserver(fitRowsToList);
        observer.observe(list);
        queueMicrotask(fitRowsToList);
        onCleanup(() => observer.disconnect());
    });

    createEffect(() => {
        if (feed.latest) queueMicrotask(fitRowsToList);
    });

    createEffect(() => {
        const id = botId();
        if (!id) return;
        const unsub = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type === 'plugin_event' && evt.payload?.source === 'sellbot') void refetchFeed();
        });
        onCleanup(unsub);
    });

    const resetPageAfter = <T,>(setter: (value: T) => void) => (value: T) => { setter(value); setPage(0); };

    const openDetail = (id: string) => navigate(`/sellbot/transactions/${id}`);

    return (
        <div class={`${styles.container} ${styles.feedPage}`}>
            <SellbotNav />
            <div class={styles.headerRow}>
                <div class="v" style={{ gap: '4px' }}>
                    <h1><Text val="SELLBOT_TRANSACTIONS_TITLE" /></h1>
                    <span class={styles.heroSubtitle}><Text val="SELLBOT_TRANSACTIONS_SUBTITLE" /></span>
                </div>
            </div>

            <div class={`fcard v ${styles.feedCard}`} style={{ gap: 'var(--spacing-md)' }}>
                <div class="h hcenter" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap' }}>
                    <input type="text" placeholder={t('SELLBOT_TXN_SEARCH_PLACEHOLDER')} value={search()} onInput={(e) => resetPageAfter(setSearch)(e.currentTarget.value)} style={{ flex: '1', 'min-width': '12rem' }} />
                    <select value={kindFilter()} onChange={(e) => resetPageAfter(setKindFilter)(e.currentTarget.value as KindFilter)}>
                        <option value="all">{t('SELLBOT_TXN_FILTER_ALL_KINDS')}</option>
                        <option value="sale">{t('SELLBOT_KIND_SALE')}</option>
                        <option value="ankauf">{t('SELLBOT_KIND_ANKAUF')}</option>
                    </select>
                    <select value={statusFilter()} onChange={(e) => resetPageAfter(setStatusFilter)(e.currentTarget.value)}>
                        <option value="">{t('SELLBOT_TXN_FILTER_ALL_STATUSES')}</option>
                        <For each={ALL_STATUSES}>{(s) => <option value={s}>{t('SELLBOT_TXN_' + s)}</option>}</For>
                    </select>
                    <Show when={multiZone()}>
                        <SellbotZoneFilter zones={zones()} value={zoneFilter()} onChange={resetPageAfter(setZoneFilter)} />
                    </Show>
                </div>
                <div class="h hcenter" style={{ gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                    <DateRangePills value={quickFilter()} onChange={resetPageAfter(setQuickFilter)} pills={QUICK_PILLS} />
                    <Show when={quickFilter() === 'custom'}>
                        <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                            <input type="date" value={customFrom()} onInput={(e) => resetPageAfter(setCustomFrom)(e.currentTarget.value)} />
                            <span class={styles.helper}>-</span>
                            <input type="date" value={customTo()} onInput={(e) => resetPageAfter(setCustomTo)(e.currentTarget.value)} />
                        </div>
                    </Show>
                </div>

                <div class={styles.feedList} ref={setListEl}>
                    <Show when={feed.latest} fallback={
                        <For each={Array.from({ length: SKELETON_ROW_COUNT })}>{() => (
                            <div class={`${styles.txnRowMerged} ${styles.txnRowSkeleton}`}>
                                <span class={styles.txnKind} />
                                <span class={styles.badge}>&nbsp;</span>
                                <span class={styles.txnPayer}>&nbsp;</span>
                                <span class={styles.txnAmount}>&nbsp;</span>
                                <span class={styles.txnTime}>&nbsp;</span>
                            </div>
                        )}</For>
                    }>
                        {(feedPage) => (
                            <Show when={feedPage().entries.length > 0} fallback={<div class={styles.emptySmall}><i class="fa-solid fa-receipt" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val="SELLBOT_NO_TRANSACTIONS" /></p></div>}>
                                <For each={feedPage().entries}>{(entry) => (
                                    <Show when={entry.sale} fallback={(() => { const a = entry.ankauf!; const summary = ankaufItemsSummary(a.items, names); return (
                                        <div class={`${styles.txnRowMerged} ${styles.clickable}`} role="button" tabindex="0" title={t('SELLBOT_DETAIL_OPEN')} onClick={() => openDetail(a.id)}>
                                            <span class={`${styles.txnKind} ${styles.txnKindAnkauf}`} title={t('SELLBOT_KIND_ANKAUF')}><i class="fa-solid fa-cart-shopping" /></span>
                                            <span class={`${styles.badge} ${sellbotStatusClass(a.status, styles)}`}>{t('SELLBOT_TXN_' + a.status)}</span>
                                            <span class={styles.txnPayer}>
                                                <MinecraftText text={a.sellerName} />
                                                <Show when={summary}><span class={styles.txnItem}> · {summary}</span></Show>
                                                <Show when={zoneLabel(a.sellbotZoneId)}>{(label) => <span class={styles.txnItem}> · <i class="fa-solid fa-location-dot" /> {label()}</span>}</Show>
                                            </span>
                                            <span class={styles.txnAmount}>{fmtMoney(a.amountPaid)}</span>
                                            <span class={styles.txnTime}>{formatDateTime(a.createdAt)}</span>
                                        </div>
                                    ); })()}>
                                        {(sale) => (
                                            <div class={`${styles.txnRowMerged} ${styles.clickable}`} role="button" tabindex="0" title={t('SELLBOT_DETAIL_OPEN')} onClick={() => openDetail(sale().id)}>
                                                <span class={`${styles.txnKind} ${styles.txnKindSale}`} title={t('SELLBOT_KIND_SALE')}><i class="fa-solid fa-hand-holding-dollar" /></span>
                                                <span class={`${styles.badge} ${sellbotStatusClass(sale().status, styles)}`}>{t('SELLBOT_TXN_' + sale().status)}</span>
                                                <span class={styles.txnPayer}>
                                                    <MinecraftText text={sale().payerName} />
                                                    <Show when={zoneLabel(sale().sellbotZoneId)}>{(label) => <span class={styles.txnItem}> · <i class="fa-solid fa-location-dot" /> {label()}</span>}</Show>
                                                </span>
                                                <span class={styles.txnAmount}>{fmtMoney(sale().amount)}</span>
                                                <span class={styles.txnTime}>{formatDateTime(sale().createdAt)}</span>
                                            </div>
                                        )}
                                    </Show>
                                )}</For>
                            </Show>
                        )}
                    </Show>
                </div>
                <Pagination page={page()} totalPages={totalPages()} onChange={setPage} />
            </div>
        </div>
    );
}
